import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Currency = 'USD' | 'AED';

interface CurrencyContextType {
  currency: Currency;
  isUAE: boolean;
  isLoading: boolean;
  formatPrice: (amount: number) => { value: number; formatted: string };
  convertToAED: (usdAmount: number) => number;
  convertToUSD: (aedAmount: number) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// UAE Dirham is pegged to USD at 3.6725
const AED_EXCHANGE_RATE = 3.6725;

// Local storage key
const CURRENCY_CACHE_KEY = 'user_currency_preference';
const CURRENCY_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

interface CachedCurrency {
  currency: Currency;
  isUAE: boolean;
  timestamp: number;
}

function getCachedCurrency(): CachedCurrency | null {
  try {
    const cached = localStorage.getItem(CURRENCY_CACHE_KEY);
    if (!cached) return null;
    
    const data: CachedCurrency = JSON.parse(cached);
    if (Date.now() - data.timestamp > CURRENCY_CACHE_TTL) {
      localStorage.removeItem(CURRENCY_CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedCurrency(currency: Currency, isUAE: boolean) {
  try {
    const data: CachedCurrency = {
      currency,
      isUAE,
      timestamp: Date.now(),
    };
    localStorage.setItem(CURRENCY_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

async function detectCountry(): Promise<{ isUAE: boolean }> {
  // Use only APIs that support CORS properly
  const endpoints: Array<{ url: string; getCode: (data: any) => string | undefined | null }> = [
    { url: 'https://ipapi.co/json/', getCode: (d) => d?.country_code },
    { url: 'https://ipwhois.app/json/', getCode: (d) => d?.country_code },
  ];

  for (const endpoint of endpoints) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(endpoint.url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) continue;

      const data = await response.json();
      const code = endpoint.getCode(data);
      if (typeof code === 'string' && code.length === 2) {
        return { isUAE: code.toUpperCase() === 'AE' };
      }
    } catch {
      // Silently continue to next endpoint
    }
  }

  return { isUAE: false };
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency>('USD');
  const [isUAE, setIsUAE] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initCurrency() {
      // Check cache first (fast path)
      const cached = getCachedCurrency();
      if (cached) {
        setCurrency(cached.currency);
        setIsUAE(cached.isUAE);
        setIsLoading(false);

        // Revalidate in the background so travel/VPN/temporary lookup failures don't lock the UI for 24h
        detectCountry().then(({ isUAE: detected }) => {
          if (cancelled) return;
          const detectedCurrency: Currency = detected ? 'AED' : 'USD';

          if (detectedCurrency !== cached.currency || detected !== cached.isUAE) {
            setCurrency(detectedCurrency);
            setIsUAE(detected);
            setCachedCurrency(detectedCurrency, detected);
          }
        });

        return;
      }

      // Detect country (cold start)
      const { isUAE: detected } = await detectCountry();
      if (cancelled) return;

      const detectedCurrency: Currency = detected ? 'AED' : 'USD';
      setCurrency(detectedCurrency);
      setIsUAE(detected);
      setCachedCurrency(detectedCurrency, detected);
      setIsLoading(false);
    }

    initCurrency();

    return () => {
      cancelled = true;
    };
  }, []);

  const convertToAED = (usdAmount: number): number => {
    return usdAmount * AED_EXCHANGE_RATE;
  };

  const convertToUSD = (aedAmount: number): number => {
    return aedAmount / AED_EXCHANGE_RATE;
  };

  const formatPrice = (usdAmount: number): { value: number; formatted: string } => {
    if (currency === 'AED') {
      const aedValue = convertToAED(usdAmount);
      return {
        value: aedValue,
        formatted: aedValue.toFixed(2),
      };
    }
    return {
      value: usdAmount,
      formatted: usdAmount.toFixed(2),
    };
  };

  return (
    <CurrencyContext.Provider value={{
      currency,
      isUAE,
      isLoading,
      formatPrice,
      convertToAED,
      convertToUSD,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
